/**
 * Blobo Entity
 *
 * The player character - a blob that jumps using tap-and-hold mechanics.
 * Phase 1: Basic jumping with charge system and squash/stretch animations.
 */

import Phaser from 'phaser';
import { GAME_CONSTANTS, GAME_EVENTS } from '../config/constants';
import { SoundManager } from '../systems/SoundManager';
import { HapticManager } from '../systems/HapticManager';

export class Blobo {
  private scene: Phaser.Scene;
  private body!: MatterJS.BodyType; // Definitely assigned in createPhysicsBody
  private graphics: Phaser.GameObjects.Graphics;
  private container: Phaser.GameObjects.Container;

  // Jump state
  public isCharging: boolean = false;
  public chargeStartTime: number = 0;
  public chargePercent: number = 0;

  // Ground detection
  public isGrounded: boolean = false;
  private groundSensor: MatterJS.BodyType | null = null;
  public currentSurfaceType: string = 'NORMAL'; // Track current platform surface type

  // MEMORY LEAK FIX (v0.8.6): Store collision event handlers for proper cleanup
  private collisionStartHandler?: (event: Phaser.Physics.Matter.Events.CollisionStartEvent) => void;
  private collisionActiveHandler?: (
    event: Phaser.Physics.Matter.Events.CollisionActiveEvent
  ) => void;
  private collisionEndHandler?: (event: Phaser.Physics.Matter.Events.CollisionEndEvent) => void;

  // Animation state
  private currentScaleX: number = 1.0;
  private currentScaleY: number = 1.0;
  private targetScaleX: number = 1.0;
  private targetScaleY: number = 1.0;
  private stretchTween: Phaser.Tweens.Tween | null = null;

  // Height tracking for Phase 5
  private startY: number = 0;
  private highestY: number = 0;

  // Eye animation (Phase 5)
  private pupilOffsetX: number = 0; // -1 to 1
  private pupilOffsetY: number = 0; // -1 to 1

  // Fall detection (Phase 5)
  private fallStartY: number = 0; // Y position when fall started
  private isFallingFast: boolean = false; // Falling faster than threshold

  // Side bounce cooldown (Performance optimization)
  private lastBounceTime: number = 0; // Timestamp of last bounce
  private readonly BOUNCE_COOLDOWN: number = 500; // ms between bounces

  // Performance: Track previous states to avoid unnecessary redraws
  private previousScaleX: number = 1.0;
  private previousScaleY: number = 1.0;
  private previousPupilX: number = 0;
  private previousPupilY: number = 0;
  private previousChargePercent: number = 0;
  private lastHeightEmit: number = 0; // Last emitted height (meters)
  private readonly HEIGHT_EMIT_THRESHOLD: number = 0.1; // Only emit when changed by 0.1m
  private hasRenderedOnce: boolean = false; // Track first render

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;
    this.startY = y;
    this.highestY = y;

    // Create container for graphics
    this.container = scene.add.container(x, y);
    this.container.setDepth(10); // Ensure player is on top

    // Create graphics object for drawing Blobo
    this.graphics = scene.add.graphics();
    this.container.add(this.graphics);

    // Create Matter.js physics body
    this.createPhysicsBody(x, y);

    // Initial render
    this.render();

    // Set up collision detection
    this.setupCollisionDetection();
  }

  /**
   * Create the physics body with ground sensor
   */
  private createPhysicsBody(x: number, y: number): void {
    const { RADIUS, FRICTION, RESTITUTION, DENSITY, AIR_RESISTANCE, INERTIA } =
      GAME_CONSTANTS.BLOBO;

    // Main circular body - marble-like rolling physics
    const mainBody = this.scene.matter.bodies.circle(x, y, RADIUS, {
      density: DENSITY,
      frictionAir: AIR_RESISTANCE,
    });

    // Ground sensor (narrower to prevent catching on platform edges)
    this.groundSensor = this.scene.matter.bodies.rectangle(
      x,
      y + RADIUS,
      RADIUS * 1.0, // Narrower sensor (was 1.5)
      3, // Thinner sensor (was 5)
      {
        isSensor: true, // Doesn't collide but detects overlaps
      }
    );

    // Compound body
    this.body = this.scene.matter.body.create({
      parts: [mainBody, this.groundSensor],
      density: DENSITY,
      frictionAir: AIR_RESISTANCE,
      slop: 0.05, // Prevent micro-collision jitter
    }) as MatterJS.BodyType;

    // Add to world
    this.scene.matter.world.add(this.body);

    // CRITICAL: Set friction and restitution AFTER adding to world
    // Must be set on the main body part (first part)
    this.scene.matter.body.setDensity(mainBody, DENSITY);
    mainBody.friction = FRICTION;
    mainBody.restitution = RESTITUTION;

    // Enable rolling - set inertia from constants (Infinity = free rotation)
    this.scene.matter.body.setInertia(this.body, INERTIA);
  }

  /**
   * Set up collision events for ground detection
   * MEMORY LEAK FIX (v0.8.6): Store handlers for proper cleanup in destroy()
   */
  private setupCollisionDetection(): void {
    // Store collision start handler for cleanup
    this.collisionStartHandler = (event: Phaser.Physics.Matter.Events.CollisionStartEvent) => {
      event.pairs.forEach(pair => {
        // Check if ground sensor is touching something
        if (pair.bodyA === this.groundSensor || pair.bodyB === this.groundSensor) {
          const otherBody = pair.bodyA === this.groundSensor ? pair.bodyB : pair.bodyA;
          // Make sure we're landing on something static (platform)
          if (otherBody.isStatic) {
            const wasAirborne = !this.isGrounded;
            this.isGrounded = true;
            this.handleLanding(otherBody, wasAirborne);
            this.scene.events.emit(GAME_EVENTS.BLOBO_LAND);
          }
        }
        // Check for side/top collisions (not ground sensor)
        else if (
          (pair.bodyA.parent === this.body || pair.bodyB.parent === this.body) &&
          pair.bodyA !== this.groundSensor &&
          pair.bodyB !== this.groundSensor
        ) {
          const otherBody = pair.bodyA.parent === this.body ? pair.bodyB : pair.bodyA;
          if (otherBody.isStatic && Math.abs(this.body.velocity.x) > 2) {
            // Side collision with significant horizontal velocity
            SoundManager.play('collision');
          }
        }
      });
    };
    this.scene.matter.world.on('collisionstart', this.collisionStartHandler);

    // Store collision active handler for cleanup
    this.collisionActiveHandler = (event: Phaser.Physics.Matter.Events.CollisionActiveEvent) => {
      // Keep grounded state while sensor is touching ground
      let foundGround = false;
      event.pairs.forEach(pair => {
        if (pair.bodyA === this.groundSensor || pair.bodyB === this.groundSensor) {
          const otherBody = pair.bodyA === this.groundSensor ? pair.bodyB : pair.bodyA;
          if (otherBody.isStatic) {
            foundGround = true;
          }
        }
      });
      if (foundGround) {
        this.isGrounded = true;
      }
    };
    this.scene.matter.world.on('collisionactive', this.collisionActiveHandler);

    // Store collision end handler for cleanup
    this.collisionEndHandler = (event: Phaser.Physics.Matter.Events.CollisionEndEvent) => {
      event.pairs.forEach(pair => {
        if (pair.bodyA === this.groundSensor || pair.bodyB === this.groundSensor) {
          // Only mark as not grounded if we're actually leaving the ground (moving up or falling)
          const velocity = this.body.velocity.y;
          if (velocity < -0.1 || velocity > 0.1) {
            this.isGrounded = false;
          }
        }
      });
    };
    this.scene.matter.world.on('collisionend', this.collisionEndHandler);
  }

  /**
   * Handle landing on a platform - dampen horizontal momentum and play sound
   */
  private handleLanding(platformBody: MatterJS.BodyType, wasAirborne: boolean): void {
    // Dampen horizontal velocity by 50% on landing to prevent excessive sliding
    const currentVelX = this.body.velocity.x;
    const currentVelY = this.body.velocity.y;
    this.scene.matter.body.setVelocity(this.body, {
      x: currentVelX * 0.5, // Keep 50% of horizontal velocity
      y: this.body.velocity.y,
    });

    // Play landing sound only if we were airborne (not continuous sliding)
    if (wasAirborne) {
      // Check friction/restitution to determine surface type
      const friction = platformBody.friction;
      const restitution = platformBody.restitution;

      // Haptic feedback based on landing velocity
      HapticManager.landing(Math.abs(currentVelY));

      // Play appropriate sound based on surface properties
      if (restitution > 1.0) {
        // Bouncy surface
        SoundManager.play('bounce');
        HapticManager.bounce();
      } else if (friction > 5.0) {
        // Sticky surface (high friction)
        SoundManager.play('sticky');
      } else if (friction < 0.1) {
        // Slippery surface (low friction)
        SoundManager.play('slide');
      } else {
        // Normal surface
        SoundManager.play('land');
      }
    }
  }

  /**
   * Start charging a jump (can charge in mid-air for skilled chaining)
   */
  public startCharge(): void {
    // Allow charging anytime (including mid-air) for skilled play
    this.isCharging = true;
    this.chargeStartTime = Date.now();
    this.chargePercent = 0;

    // Cancel any stretch animation
    if (this.stretchTween) {
      this.stretchTween.stop();
      this.stretchTween = null;
    }
  }

  /**
   * Update charge during hold (called each frame)
   */
  public updateCharge(_delta: number): void {
    if (!this.isCharging) return;

    const holdDuration = (Date.now() - this.chargeStartTime) / 1000; // Convert to seconds
    const { MAX_CHARGE_TIME } = GAME_CONSTANTS.JUMP;

    // Clamp and calculate charge percentage
    const clampedTime = Phaser.Math.Clamp(holdDuration, 0, MAX_CHARGE_TIME);
    this.chargePercent = clampedTime / MAX_CHARGE_TIME;

    // Update haptic feedback intensity
    HapticManager.startCharging(this.chargePercent);

    // Update squash animation
    const { SQUASH_MIN_SCALE_Y, SQUASH_MAX_SCALE_X } = GAME_CONSTANTS.BLOBO;
    this.targetScaleY = Phaser.Math.Linear(1.0, SQUASH_MIN_SCALE_Y, this.chargePercent);
    this.targetScaleX = Phaser.Math.Linear(1.0, SQUASH_MAX_SCALE_X, this.chargePercent);
  }

  /**
   * Release charge and execute jump
   */
  public releaseCharge(): void {
    if (!this.isCharging) return;
    this.isCharging = false;

    // Stop charging haptic feedback
    HapticManager.stopCharging();

    // If not grounded, lose the charge without jumping (requires timing skill)
    if (!this.isGrounded) {
      this.chargePercent = 0;
      // Reset scale animation back to normal
      this.targetScaleX = 1.0;
      this.targetScaleY = 1.0;
      return;
    }

    const holdDuration = (Date.now() - this.chargeStartTime) / 1000;
    const { MIN_CHARGE_TIME, MAX_CHARGE_TIME, MIN_VELOCITY, MAX_VELOCITY } = GAME_CONSTANTS.JUMP;

    // Calculate charge percentage
    const chargeTime = Phaser.Math.Clamp(holdDuration, MIN_CHARGE_TIME, MAX_CHARGE_TIME);

    this.chargePercent = (chargeTime - MIN_CHARGE_TIME) / (MAX_CHARGE_TIME - MIN_CHARGE_TIME);

    // Calculate jump velocity based on charge
    let jumpVelocity = Phaser.Math.Linear(MIN_VELOCITY, MAX_VELOCITY, this.chargePercent);

    // Apply jump multiplier for sticky platforms (reduces jump height)
    if (this.currentSurfaceType === 'STICKY') {
      const stickyMultiplier = GAME_CONSTANTS.SURFACE.STICKY.JUMP_MULTIPLIER || 0.7;
      jumpVelocity *= stickyMultiplier;
    }

    // Set upward velocity directly (instant jump)
    this.scene.matter.body.setVelocity(this.body, {
      x: this.body.velocity.x, // Preserve horizontal velocity
      y: jumpVelocity, // Set upward velocity
    });

    // Play jump sound with dynamic pitch based on charge
    SoundManager.playJump(this.chargePercent);

    // Haptic feedback for jump release (scaled by charge)
    HapticManager.jumpRelease(this.chargePercent);

    // Trigger stretch animation
    this.playStretchAnimation();

    // Emit jump event
    this.scene.events.emit(GAME_EVENTS.BLOBO_JUMP, {
      velocity: jumpVelocity,
      charge: this.chargePercent,
    });

    // Reset charging state
    this.chargePercent = 0;
    this.isGrounded = false; // We're in the air now
  }

  /**
   * Play stretch animation on jump
   */
  private playStretchAnimation(): void {
    const { STRETCH_SCALE_Y, STRETCH_SCALE_X, STRETCH_DURATION } = GAME_CONSTANTS.BLOBO;

    // Cancel any existing tween
    if (this.stretchTween) {
      this.stretchTween.stop();
    }

    // Quick stretch then return to normal
    this.targetScaleY = STRETCH_SCALE_Y;
    this.targetScaleX = STRETCH_SCALE_X;

    // After a short duration, return to normal
    this.stretchTween = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: STRETCH_DURATION,
      onUpdate: tween => {
        const progress = tween.getValue() ?? 0;
        // Interpolate back to normal scale
        this.targetScaleY = Phaser.Math.Linear(STRETCH_SCALE_Y, 1.0, progress);
        this.targetScaleX = Phaser.Math.Linear(STRETCH_SCALE_X, 1.0, progress);
      },
      onComplete: () => {
        this.targetScaleX = 1.0;
        this.targetScaleY = 1.0;
        this.stretchTween = null;
      },
    });
  }

  /**
   * Update Blobo each frame
   * Performance: Minimize per-frame calculations for mobile
   */
  public update(_time: number, delta: number): void {
    // Sync container position (NO rotation for performance on mobile)
    this.container.setPosition(this.body.position.x, this.body.position.y);

    // Update charging animation
    if (this.isCharging) {
      this.updateCharge(delta);
    }

    // Smooth scale transitions (only when charging/jumping)
    if (
      this.isCharging ||
      Math.abs(this.currentScaleX - 1.0) > 0.01 ||
      Math.abs(this.currentScaleY - 1.0) > 0.01
    ) {
      const lerpSpeed = 0.2;
      this.currentScaleX = Phaser.Math.Linear(this.currentScaleX, this.targetScaleX, lerpSpeed);
      this.currentScaleY = Phaser.Math.Linear(this.currentScaleY, this.targetScaleY, lerpSpeed);
    }

    // Update eye animation (Phase 5) - throttled
    this.updateEyeAnimation();

    // Height tracking (Phase 5)
    this.trackHeightChange();

    // Side bounce mechanic - prevent falling off sides
    this.applySideBounce();

    // Re-render (optimized to skip when no changes)
    this.render();
  }

  /**
   * Update eye pupil animation based on state (Phase 5)
   */
  private updateEyeAnimation(): void {
    let targetPupilX = 0;
    let targetPupilY = 0;

    // Charging - look up
    if (this.isCharging) {
      targetPupilY = -0.8; // Look up
    }
    // Falling - look down
    else if (this.body.velocity.y > 5) {
      targetPupilY = 0.8; // Look down
    }
    // Jumping or in air - look in direction of movement
    else if (!this.isGrounded && Math.abs(this.body.velocity.x) > 1) {
      // Look in horizontal direction
      targetPupilX = Phaser.Math.Clamp(this.body.velocity.x / 10, -1, 1);
      targetPupilY = -0.3; // Slightly up when in air
    }
    // Grounded - centered
    else {
      targetPupilX = 0;
      targetPupilY = 0;
    }

    // Smooth transition
    const eyeLerpSpeed = 0.15;
    this.pupilOffsetX = Phaser.Math.Linear(this.pupilOffsetX, targetPupilX, eyeLerpSpeed);
    this.pupilOffsetY = Phaser.Math.Linear(this.pupilOffsetY, targetPupilY, eyeLerpSpeed);
  }

  /**
   * Track height changes and emit events (Phase 5)
   * Performance: Only emit when height changes by threshold to reduce event spam
   */
  private trackHeightChange(): void {
    // Calculate current height in pixels from start position
    const currentHeightPx = this.startY - this.body.position.y;

    // Convert to meters using HEIGHT_SCALE (1m = 90px)
    const currentHeightMeters = currentHeightPx / GAME_CONSTANTS.BLOBO.HEIGHT_SCALE;

    // Calculate max height reached
    const maxHeightPx = this.startY - this.highestY;
    const maxHeightMeters = maxHeightPx / GAME_CONSTANTS.BLOBO.HEIGHT_SCALE;

    // Update highest Y if we've reached a new height (Y decreases as we go up)
    if (this.body.position.y < this.highestY) {
      this.highestY = this.body.position.y;
    }

    // PERFORMANCE: Only emit when height changes significantly (reduces 60fps → ~10fps)
    const heightDiff = Math.abs(currentHeightMeters - this.lastHeightEmit);
    if (heightDiff >= this.HEIGHT_EMIT_THRESHOLD) {
      this.scene.events.emit(GAME_EVENTS.BLOBO_HEIGHT_CHANGE, {
        currentHeight: currentHeightMeters,
        maxHeight: maxHeightMeters,
      });
      this.lastHeightEmit = currentHeightMeters;
    }

    // Fall detection - track significant falls
    const FALL_VELOCITY_THRESHOLD = 3; // Falling faster than this triggers detection
    const isFallingNow = this.body.velocity.y > FALL_VELOCITY_THRESHOLD;

    if (isFallingNow && !this.isFallingFast) {
      // Started falling fast - record starting position
      this.fallStartY = this.body.position.y;
      this.isFallingFast = true;
    } else if (!isFallingNow && this.isFallingFast) {
      // Stopped falling fast - calculate fall distance
      const fallDistancePx = this.body.position.y - this.fallStartY;

      if (fallDistancePx > 0) {
        // Emit fall event with distance in pixels
        this.scene.events.emit(GAME_EVENTS.BLOBO_FALL, { fallDistance: fallDistancePx });
      }

      this.isFallingFast = false;
    }
  }

  /**
   * Apply side bounce when falling near world edges
   * Performance: Uses cooldown to prevent calling setVelocity every frame
   * Safety: Only activates after falling 2m+ to avoid interference with normal gameplay
   */
  private applySideBounce(): void {
    const WORLD_WIDTH = 800; // World width from GameScene
    const BOUNCE_ZONE = 50; // Distance from edge to trigger bounce (pixels)
    const BOUNCE_FORCE = 5; // Horizontal velocity to push toward center
    const MIN_FALL_DISTANCE = 180; // Only activate after falling 2m (2 * 90px = 180px)

    // Only bounce when falling (velocity.y > 0 means falling down in Phaser)
    if (this.body.velocity.y > 0) {
      // Calculate fall distance from last landing or fall start
      const currentFallDistance = this.isFallingFast ? this.body.position.y - this.fallStartY : 0;

      // Only activate bounce after falling at least 2m (safety net, not normal gameplay)
      if (currentFallDistance < MIN_FALL_DISTANCE) {
        return;
      }

      const x = this.body.position.x;
      const now = Date.now();

      // Check cooldown to prevent spamming setVelocity (performance)
      if (now - this.lastBounceTime < this.BOUNCE_COOLDOWN) {
        return;
      }

      // Near left edge - bounce right
      if (x < BOUNCE_ZONE) {
        this.scene.matter.body.setVelocity(this.body, {
          x: BOUNCE_FORCE,
          y: this.body.velocity.y,
        });
        this.lastBounceTime = now;
      }
      // Near right edge - bounce left
      else if (x > WORLD_WIDTH - BOUNCE_ZONE) {
        this.scene.matter.body.setVelocity(this.body, {
          x: -BOUNCE_FORCE,
          y: this.body.velocity.y,
        });
        this.lastBounceTime = now;
      }
    }
  }

  /**
   * Get current height from start position (Phase 5)
   */
  public getCurrentHeight(): number {
    const heightPx = this.startY - this.body.position.y;
    return heightPx / GAME_CONSTANTS.BLOBO.HEIGHT_SCALE;
  }

  /**
   * Get the original ground level Y position (Phase 5)
   */
  public getStartY(): number {
    return this.startY;
  }

  /**
   * Set the original ground level Y position (Phase 5 - for resume functionality)
   */
  public setStartY(startY: number): void {
    this.startY = startY;
  }

  /**
   * Set the highest Y position reached (Phase 5 - for resume functionality)
   * Pass maxHeight in meters to calculate highestY
   */
  public setHighestYFromMaxHeight(maxHeightMeters: number): void {
    const maxHeightPx = maxHeightMeters * GAME_CONSTANTS.BLOBO.HEIGHT_SCALE;
    this.highestY = this.startY - maxHeightPx;
  }

  /**
   * Render Blobo graphics
   * Performance: Only redraw when visual state actually changes
   */
  private render(): void {
    // PERFORMANCE: Check if anything changed before expensive redraw
    // BUT always render the first time to make Blobo visible
    if (this.hasRenderedOnce) {
      const scaleChanged =
        Math.abs(this.currentScaleX - this.previousScaleX) > 0.01 ||
        Math.abs(this.currentScaleY - this.previousScaleY) > 0.01;
      const pupilChanged =
        Math.abs(this.pupilOffsetX - this.previousPupilX) > 0.01 ||
        Math.abs(this.pupilOffsetY - this.previousPupilY) > 0.01;
      const chargeChanged = Math.abs(this.chargePercent - this.previousChargePercent) > 0.01;

      // If nothing changed, skip the expensive redraw
      if (!scaleChanged && !pupilChanged && !chargeChanged) {
        return;
      }
    }

    // Update previous states
    this.previousScaleX = this.currentScaleX;
    this.previousScaleY = this.currentScaleY;
    this.previousPupilX = this.pupilOffsetX;
    this.previousPupilY = this.pupilOffsetY;
    this.previousChargePercent = this.chargePercent;

    // Perform the redraw
    this.graphics.clear();

    const radius = GAME_CONSTANTS.BLOBO.RADIUS;
    const scaleX = this.currentScaleX;
    const scaleY = this.currentScaleY;

    // Body circle (white with black outline)
    this.graphics.fillStyle(0xffffff, 1);
    this.graphics.lineStyle(1, 0x000000, 1);

    // Draw ellipse for squash/stretch effect
    this.graphics.fillEllipse(0, 0, radius * 2 * scaleX, radius * 2 * scaleY);
    this.graphics.strokeEllipse(0, 0, radius * 2 * scaleX, radius * 2 * scaleY);

    // Googly Eyes (larger, with white background and animated pupils)
    const eyeY = -(radius * 0.35) * scaleY;
    const eyeRadius = Math.max(3, radius * 0.5); // Much larger eyes
    const eyeSpacing = radius * 0.45 * scaleX;

    // Left eye
    this.graphics.fillStyle(0xffffff, 1); // White background
    this.graphics.lineStyle(1.5, 0x000000, 1);
    this.graphics.fillCircle(-eyeSpacing, eyeY, eyeRadius);
    this.graphics.strokeCircle(-eyeSpacing, eyeY, eyeRadius);

    // Left pupil (animated)
    const pupilRadius = eyeRadius * 0.5;
    const pupilX = -eyeSpacing + this.pupilOffsetX * eyeRadius * 0.4;
    const pupilY = eyeY + this.pupilOffsetY * eyeRadius * 0.4;
    this.graphics.fillStyle(0x000000, 1);
    this.graphics.fillCircle(pupilX, pupilY, pupilRadius);

    // Right eye
    this.graphics.fillStyle(0xffffff, 1);
    this.graphics.lineStyle(1.5, 0x000000, 1);
    this.graphics.fillCircle(eyeSpacing, eyeY, eyeRadius);
    this.graphics.strokeCircle(eyeSpacing, eyeY, eyeRadius);

    // Right pupil (animated)
    const pupilX2 = eyeSpacing + this.pupilOffsetX * eyeRadius * 0.4;
    const pupilY2 = eyeY + this.pupilOffsetY * eyeRadius * 0.4;
    this.graphics.fillStyle(0x000000, 1);
    this.graphics.fillCircle(pupilX2, pupilY2, pupilRadius);

    // Charge indicator (circular progress bar)
    if (this.isCharging && this.chargePercent > 0) {
      const chargeRadius = radius + 3; // Smaller gap for small size
      const chargeAngle = this.chargePercent * Phaser.Math.PI2;

      // Greenish charge indicator
      this.graphics.lineStyle(2, 0x00ff00, 0.9); // Thinner, more opaque
      this.graphics.beginPath();
      this.graphics.arc(
        0,
        0,
        chargeRadius,
        -Math.PI / 2, // Start at top
        -Math.PI / 2 + chargeAngle,
        false
      );
      this.graphics.strokePath();
    }

    // Mark that we've rendered at least once
    this.hasRenderedOnce = true;
  }

  /**
   * Get current position
   */
  public getPosition(): { x: number; y: number } {
    return {
      x: this.body.position.x,
      y: this.body.position.y,
    };
  }

  /**
   * Get the container for camera following
   */
  public getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Get the physics body
   */
  public getBody(): MatterJS.BodyType {
    return this.body;
  }

  /**
   * Clean up
   * MEMORY LEAK FIX (v0.8.6): Remove collision event listeners to prevent accumulation
   */
  public destroy(): void {
    if (this.stretchTween) {
      this.stretchTween.stop();
    }

    // CRITICAL FIX: Remove collision event listeners before destroying
    // Without this, handlers accumulate on every game restart causing exponential FPS loss
    if (this.scene.matter?.world) {
      if (this.collisionStartHandler) {
        this.scene.matter.world.off('collisionstart', this.collisionStartHandler);
        this.collisionStartHandler = undefined;
      }
      if (this.collisionActiveHandler) {
        this.scene.matter.world.off('collisionactive', this.collisionActiveHandler);
        this.collisionActiveHandler = undefined;
      }
      if (this.collisionEndHandler) {
        this.scene.matter.world.off('collisionend', this.collisionEndHandler);
        this.collisionEndHandler = undefined;
      }

      // Remove physics body from world
      this.scene.matter.world.remove(this.body);
    }

    this.graphics.destroy();
    this.container.destroy();
  }
}
